import React, { useState, useEffect, useRef, useCallback } from 'react';
import PageTransition from '../components/Shared/PageTransition';
import ServerInfoCard from '../components/Shared/ServerInfoCard';
import RconHero from '../components/RconHacking/RconHero';
import RconStagesTracker from '../components/RconHacking/RconStagesTracker';
import RconDirectConnect from '../components/RconHacking/RconDirectConnect';
import RconScannerSettings from '../components/RconHacking/RconScannerSettings';
import RconTerminal from '../components/RconHacking/RconTerminal';
import RconProTips from '../components/RconHacking/RconProTips';
import MusicControlPanel from '../components/Shared/MusicControlPanel';
import GameContainer from '../components/Shared/GameContainer';
import { useBackgroundMusic } from '../hooks/useBackgroundMusic';

import { useServerConnection } from '../contexts/ServerConnectionContext';
import { useAuth } from '../contexts/AuthContext';
import useWebSocket from '../hooks/useWebSocket';
import { RCON_MESSAGE_TYPES, RCON_TOPICS, getRconAppEndpoint, getRconUserQueueTopic } from '../config/wsRconConfig';
import TermsOfServiceModal from '../components/Shared/TermsOfServiceModal';
import { useNotifications } from '../contexts/NotificationContext';
import { useLanguage } from '../contexts/LanguageContext';
import { handleWebSocketError } from '../utils/websocketErrorHandler';

import './RconPage.css';

const formatTimeStr = (date = new Date()) => {
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  return `[${h}:${m}:${s}]`;
};

const cleanIp = (ip) => {
  if (!ip) return '';
  const trimmed = String(ip).trim();
  if (trimmed.includes(':')) {
    return trimmed.split(':')[0];
  }
  return trimmed;
};

const RconHackingPage = ({ openModal }) => {
  const { language, t } = useLanguage();
  const isEn = language === 'en';
  const { isAuthenticated, triggerGoogleLogin } = useAuth();
  const { isConnected, sendMessage, subscribe } = useWebSocket();
  const { addNotification, showToast } = useNotifications();
  const { serverInfo, targetIp } = useServerConnection();

  // --- Target info (dynamically derived from connected server) ---
  const currentHost = serverInfo?.hostname || serverInfo?.ip || targetIp || '';
  const currentPort = serverInfo?.port || null;
  const fullTargetAddress = currentHost ? (currentPort ? `${currentHost}:${currentPort}` : currentHost) : '';

  const DEFAULT_WORDLISTS = ['base1', 'base2'];

  // --- Configuration State ---
  const [portStart, setPortStart] = useState(25500);
  const [portEnd, setPortEnd] = useState(26000);
  const [threads, setThreads] = useState(30);
  const [selectedDict, setSelectedDict] = useState(() => {
    try {
      const cached = localStorage.getItem('rcon_selected_dict');
      if (cached) return cached;
    } catch (e) {}
    return 'base1';
  });
  const [isMutator, setIsMutator] = useState(true);
  const [availableWordlists, setAvailableWordlists] = useState(() => {
    try {
      const cached = localStorage.getItem('rcon_available_wordlists');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) {}
    return DEFAULT_WORDLISTS;
  });

  // --- Audit Process & Pipeline State ---
  const [currentStage, setCurrentStage] = useState('IDLE'); // 'IDLE' | 'SCAN' | 'CRACK' | 'ACCESS'
  const [isRunning, setIsRunning] = useState(false);
  const music = useBackgroundMusic(isRunning);
  const [foundPorts, setFoundPorts] = useState([]);
  const [selectedPort, setSelectedPort] = useState(null);
  const [rconHackedData, setRconHackedData] = useState(null);

  // --- Metrics State ---
  const [speed, setSpeed] = useState('0 p/s');
  const [testedCount, setTestedCount] = useState('0 / 0');
  const [portsFoundCount, setPortsFoundCount] = useState(0);
  const [sessionSeconds, setSessionSeconds] = useState(0);

  // --- Terminal Logs State ---
  const [logs, setLogs] = useState(() => [
    {
      time: '[00:00:00]',
      tagClass: 'info',
      tagText: '[SYSTEM]',
      text: 'Демон аудита StormBlack v4.8 инициализирован.',
      isHighlight: false
    },
    {
      time: '[00:00:00]',
      tagClass: 'scan',
      tagText: '[HINT]',
      text: 'Подключитесь к целевому серверу в панели выше, чтобы начать аудит RCON.',
      isHighlight: false
    }
  ]);

  // --- Terms of Service Modal State ---
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'audit' | { type: 'direct', port, pwd }

  const addLog = useCallback((tagClass, tagText, text, isHighlight = false) => {
    setLogs(prev => {
      const updated = [...prev, { time: formatTimeStr(), tagClass, tagText, text, isHighlight }];
      return updated.length > 400 ? updated.slice(updated.length - 400) : updated;
    });
  }, []);

  // Update target in logs when a server is selected / connected
  useEffect(() => {
    if (fullTargetAddress) {
      addLog('info', '[TARGET]', `Целевой сервер: ${fullTargetAddress} • Готов к сканированию.`, true);
    }
  }, [fullTargetAddress, addLog]);

  // Session timer ref
  const clockTimerRef = useRef(null);

  const startClock = useCallback(() => {
    setSessionSeconds(0);
    clearInterval(clockTimerRef.current);
    clockTimerRef.current = setInterval(() => {
      setSessionSeconds(s => s + 1);
    }, 1000);
  }, []);

  const stopClock = useCallback(() => {
    clearInterval(clockTimerRef.current);
  }, []);

  const formatSessionTime = (seconds) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, '0');
    const secs = String(seconds % 60).padStart(2, '0');
    return `${mins}:${secs}`;
  };

  // --- Real WebSocket Subscriptions ---
  const rconProgressSubRef = useRef(null);
  const rconErrorSubRef = useRef(null);
  const rconResultSubRef = useRef(null);
  const lastProgressRef = useRef({ count: 0, time: 0 });
  const hasFoundPortRef = useRef(false);
  const currentStageRef = useRef('IDLE');
  const foundPortsRef = useRef([]);

  useEffect(() => {
    currentStageRef.current = currentStage;
  }, [currentStage]);

  useEffect(() => {
    foundPortsRef.current = foundPorts;
  }, [foundPorts]);

  // Fast HTTP sync for bases (instant, works without WS auth or handshake delay)
  useEffect(() => {
    fetch('/api/public/rcon/bases')
      .then(res => res.ok ? res.json() : null)
      .then(bases => {
        if (Array.isArray(bases) && bases.length > 0) {
          setAvailableWordlists(bases);
          try {
            localStorage.setItem('rcon_available_wordlists', JSON.stringify(bases));
          } catch (e) {}
          setSelectedDict(prev => (!prev || !bases.includes(prev) ? bases[0] : prev));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isConnected || !isAuthenticated) return;

    let basesReceived = false;
    let retryIntervalRef = null;

    rconProgressSubRef.current = subscribe(
      getRconUserQueueTopic(RCON_TOPICS.USER_PROGRESS),
      (message) => {
        if (!message) return;
        const { type, data } = message;

        if (type === RCON_MESSAGE_TYPES.PASSWORD_BASES_LIST && Array.isArray(data?.bases)) {
          basesReceived = true;
          if (retryIntervalRef) clearInterval(retryIntervalRef);
          setAvailableWordlists(data.bases);
          try {
            localStorage.setItem('rcon_available_wordlists', JSON.stringify(data.bases));
          } catch (e) {}
          addLog('info', '[WORDLIST]', `Доступные базы словарей с сервера: ${data.bases.join(', ')}`);
          setSelectedDict(prev => {
            if (!prev || !data.bases.includes(prev)) {
              return data.bases[0] || '';
            }
            return prev;
          });
        }

        if (type === RCON_MESSAGE_TYPES.PORT_SCAN_STARTED) {
          setIsRunning(true);
          startClock();
          setCurrentStage('SCAN');
          hasFoundPortRef.current = false;
          setFoundPorts([]);
          setPortsFoundCount(0);
          addLog('info', '[START]', 'Запущено сканирование открытых портов RCON...', true);
        }

        if (type === RCON_MESSAGE_TYPES.PORT_FOUND && data?.port != null) {
          const newPort = Number(data.port);
          hasFoundPortRef.current = true;
          setFoundPorts(prev => {
            if (!prev.some(p => (typeof p === 'object' ? p.port : p) === newPort)) {
              const updated = [...prev, { port: newPort }].sort((a, b) => a.port - b.port);
              setPortsFoundCount(updated.length);
              return updated;
            }
            return prev;
          });
          addLog('open', '[OPEN]', `Обнаружен активный порт RCON: ${newPort}`, true);
          showToast(isEn ? `Found RCON port: ${newPort}` : `Обнаружен порт RCON: ${newPort}`);
        }

        if (type === RCON_MESSAGE_TYPES.PASSWORD_CRACK_STARTED) {
          hasFoundPortRef.current = true;
          setCurrentStage('CRACK');
          const crackPort = data?.port || selectedPort || 25575;
          addLog('crack', '[CRACK]', `Запущен подбор паролей для порта ${crackPort}...`, true);
        }

        if (type === RCON_MESSAGE_TYPES.STATUS_UPDATE && data?.status) {
          addLog('info', '[STATUS]', `Статус: ${data.status}`);
        }

        if (type === RCON_MESSAGE_TYPES.PROGRESS_UPDATE && data?.message) {
          addLog('probe', '[PROGRESS]', data.message);
          const msg = data.message;
          const countMatch = msg.match(/(\d+)\s*\/\s*(\d+)/);
          if (countMatch) {
            const currentCount = parseInt(countMatch[1], 10);
            setTestedCount(`${countMatch[1]} / ${countMatch[2]}`);

            const now = Date.now();
            if (lastProgressRef.current.time > 0 && currentCount >= lastProgressRef.current.count) {
              const elapsedSec = (now - lastProgressRef.current.time) / 1000;
              if (elapsedSec > 0.2) {
                const calculatedSpeed = Math.round((currentCount - lastProgressRef.current.count) / elapsedSec);
                if (calculatedSpeed >= 0) {
                  setSpeed(`${calculatedSpeed.toLocaleString()} p/s`);
                }
                lastProgressRef.current = { count: currentCount, time: now };
              }
            } else {
              lastProgressRef.current = { count: currentCount, time: now };
            }
          }
          const speedMatch = msg.match(/(\d+)\s*p\/s/i);
          if (speedMatch) {
            const val = parseInt(speedMatch[1], 10);
            setSpeed(`${val.toLocaleString()} p/s`);
          }
        }

        if (type === RCON_MESSAGE_TYPES.RCON_COMMAND_RESPONSE && data?.response != null) {
          data.response.split('\n').forEach(line => {
            if (line.trim()) addLog('info', '[SERVER]', line.trim());
          });
        }
      }
    );

    rconResultSubRef.current = subscribe(
      getRconUserQueueTopic(RCON_TOPICS.USER_RESULT),
      (message) => {
        if (!message) return;
        const { type, data } = message;

        if (type === RCON_MESSAGE_TYPES.PORT_SCAN_COMPLETED) {
          const foundAny = !!data?.portsFound || hasFoundPortRef.current || (foundPortsRef.current && foundPortsRef.current.length > 0) || currentStageRef.current === 'CRACK';
          if (foundAny) {
            setCurrentStage('CRACK');
            addLog('info', '[SCAN DONE]', 'Сканирование портов завершено. Найдены активные RCON порты.', true);
          } else {
            addLog('info', '[SCAN DONE]', 'Сканирование портов завершено. Открытые порты не обнаружены.', true);
            setIsRunning(false);
            stopClock();
            setCurrentStage('IDLE');
          }
        }

        if (type === RCON_MESSAGE_TYPES.PASSWORD_FOUND || type === RCON_MESSAGE_TYPES.SERVER_HACKED) {
          setRconHackedData(data);
          setCurrentStage('ACCESS');
          setIsRunning(false);
          stopClock();
          setSpeed('0 p/s');
          addLog('success', '[SUCCESS]', `Пароль подобран! Доступ к порту ${data?.port || 25575} получен. Пароль: ${data?.password}`, true);
          addLog('scan', '[READY]', 'Интерактивная командная строка готова к выполнению команд.', true);
          showToast(isEn ? 'Access granted! Interactive shell ready' : 'Доступ получен! RCON-консоль активна');
          window.dispatchEvent(new CustomEvent('showAiInvite', {
            detail: { message: t('aiAssistantInviteRcon') }
          }));
        }

        if (type === RCON_MESSAGE_TYPES.PASSWORD_CRACK_FAILED) {
          setIsRunning(false);
          stopClock();
          setSpeed('0 p/s');
          setCurrentStage('IDLE');
          addLog('warn', '[FAILED]', `Подбор пароля для порта ${data?.port || ''} не увенчался успехом.`);
          showToast(isEn ? 'Password crack failed' : 'Пароль не найден');
          window.dispatchEvent(new CustomEvent('showAiInvite', {
            detail: { message: t('aiAssistantInviteRcon') }
          }));
        }

        if (type === RCON_MESSAGE_TYPES.BRUTE_STOPPED) {
          setIsRunning(false);
          stopClock();
          setSpeed('0 p/s');
          setCurrentStage('IDLE');
          addLog('warn', '[STOPPED]', data?.message || 'Процесс аудита остановлен.');
        }
      }
    );

    rconErrorSubRef.current = subscribe(
      getRconUserQueueTopic(RCON_TOPICS.USER_ERROR),
      (errorData) => {
        setIsRunning(false);
        stopClock();
        setSpeed('0 p/s');
        setCurrentStage('IDLE');
        const errorMessage = handleWebSocketError(errorData, null, openModal, addNotification, {
          defaultTitle: 'RCON Error',
          moduleName: 'RCON Audit'
        });
        addLog('warn', '[ERROR]', errorMessage || 'Ошибка выполнения аудита RCON');
      }
    );

    // Small delay (60ms) to ensure subscription frame has been registered by STOMP broker
    const initialTimer = setTimeout(() => {
      if (!basesReceived) {
        sendMessage(getRconAppEndpoint(RCON_TOPICS.APP_GET_BASES), {});
      }
    }, 60);

    // Periodic retry every 2s up to 3 times if server response was dropped
    let attempts = 0;
    retryIntervalRef = setInterval(() => {
      attempts++;
      if (basesReceived || attempts > 3) {
        clearInterval(retryIntervalRef);
        return;
      }
      sendMessage(getRconAppEndpoint(RCON_TOPICS.APP_GET_BASES), {});
    }, 2000);

    return () => {
      clearTimeout(initialTimer);
      if (retryIntervalRef) clearInterval(retryIntervalRef);
      rconProgressSubRef.current?.();
      rconResultSubRef.current?.();
      rconErrorSubRef.current?.();
    };
  }, [isConnected, isAuthenticated, subscribe, sendMessage, addLog, stopClock, isEn, showToast, selectedPort, openModal, addNotification, startClock, t]);

  // --- Real Audit Controls ---
  const stopAudit = useCallback((reason = 'Процесс остановлен пользователем.') => {
    stopClock();
    setIsRunning(false);
    setSpeed('0 p/s');
    lastProgressRef.current = { count: 0, time: 0 };
    setCurrentStage('IDLE');
    addLog('warn', '[STOP]', reason);
    showToast(isEn ? 'Audit stopped' : 'Процесс аудита остановлен');

    if (isConnected && serverInfo) {
      const host = cleanIp(targetIp || serverInfo.ip || serverInfo.hostname);
      sendMessage(getRconAppEndpoint(RCON_TOPICS.APP_STOP), { ip: host });
    }
  }, [stopClock, addLog, showToast, isEn, isConnected, serverInfo, targetIp, sendMessage]);

  const startAudit = useCallback(() => {
    if (!serverInfo || !serverInfo.online) {
      addLog('warn', '[ALERT]', 'Сначала подключитесь к целевому серверу в панели выше.');
      showToast(isEn ? 'Please connect to a target server first' : 'Сначала подключитесь к целевому серверу');
      return;
    }

    if (!isAuthenticated) {
      addLog('warn', '[AUTH]', 'Требуется авторизация для запуска аудита.');
      triggerGoogleLogin();
      return;
    }

    if (!isConnected) {
      addLog('warn', '[NETWORK]', 'Отсутствует WebSocket-соединение с сервером.');
      showToast(isEn ? 'WebSocket disconnected' : 'Нет соединения с сервером');
      return;
    }

    const activeBase = selectedDict || (availableWordlists && availableWordlists[0]);
    if (!activeBase) {
      addLog('warn', '[ALERT]', 'Базы паролей еще загружаются с сервера или отсутствуют.');
      showToast(isEn ? 'Password bases are still loading...' : 'Базы паролей еще загружаются...');
      return;
    }

    setIsRunning(true);
    startClock();
    setSpeed('0 p/s');
    lastProgressRef.current = { count: 0, time: Date.now() };
    setTestedCount('0 / 0');
    setPortsFoundCount(0);
    setFoundPorts([]);
    setRconHackedData(null);
    setCurrentStage('SCAN');

    const host = cleanIp(targetIp || serverInfo.ip || serverInfo.hostname);
    const sPort = serverInfo.port || 25565;
    const pStart = Number(portStart) || 25500;
    const pEnd = Number(portEnd) || 26000;

    let computedScanMode = 'FULL';
    if (pStart === 25500 && pEnd === 26000) computedScanMode = 'FAST';
    else if (pStart === 25500 && pEnd === 30000) computedScanMode = 'MIDDLE';
    else if (pStart === 1024 && pEnd === 65535) computedScanMode = 'FULL';

    addLog('info', '[START]', `Инициализация сканирования ${host}:${sPort} (${threads} потоков)...`, true);
    addLog('scan', '[CONFIG]', `База паролей: «${activeBase}» | Диапазон: [${pStart} — ${pEnd}]`);

    sendMessage(getRconAppEndpoint(RCON_TOPICS.APP_START), {
      server: { ip: host, port: sPort },
      settings: {
        scanMode: computedScanMode,
        portStart: pStart,
        portEnd: pEnd,
        timeOut: 1200,
        passwordBase: activeBase,
        speedMode: (threads > 1),
        threadsNumb: threads,
        selectRconPort: !!selectedPort,
        rconPort: selectedPort || 0,
        selectRconData: false,
        password: '',
        secure: false
      }
    });
  }, [serverInfo, isAuthenticated, isConnected, targetIp, threads, portStart, portEnd, selectedDict, availableWordlists, selectedPort, sendMessage, startClock, addLog, showToast, isEn, triggerGoogleLogin]);

  const handleToggleAudit = useCallback(() => {
    if (isRunning) {
      stopAudit();
    } else {
      if (!serverInfo || !serverInfo.online) {
        addLog('warn', '[ALERT]', 'Сначала подключитесь к целевому серверу в панели выше.');
        showToast(isEn ? 'Please connect to a target server first' : 'Сначала подключитесь к целевому серверу');
        return;
      }
      if (!isAuthenticated) {
        addLog('warn', '[AUTH]', 'Требуется авторизация для запуска аудита.');
        triggerGoogleLogin();
        return;
      }
      setPendingAction('audit');
      setIsTermsModalOpen(true);
    }
  }, [isRunning, stopAudit, serverInfo, isAuthenticated, isEn, addLog, showToast, triggerGoogleLogin]);

  // Real Direct Connect Handler
  const executeDirectConnect = useCallback((port, pwd) => {
    const host = cleanIp(targetIp || serverInfo.ip || serverInfo.hostname);
    addLog('info', '[CONNECT]', `Прямое подключение к RCON ${host}:${port}...`, true);
    setIsRunning(true);
    startClock();

    sendMessage(getRconAppEndpoint(RCON_TOPICS.APP_MANUAL_CONNECT), {
      ip: host,
      port: Number(port),
      serverPort: serverInfo.port || 25565,
      password: pwd
    });
  }, [targetIp, serverInfo, addLog, startClock, sendMessage]);

  const handleDirectConnect = useCallback((port, pwd) => {
    if (!serverInfo || !serverInfo.online) {
      addLog('warn', '[ALERT]', 'Сначала подключитесь к целевому серверу в панели выше.');
      showToast(isEn ? 'Please connect to a target server first' : 'Сначала подключитесь к целевому серверу');
      return;
    }

    if (!isAuthenticated) {
      triggerGoogleLogin();
      return;
    }

    if (!isConnected) {
      showToast(isEn ? 'WebSocket disconnected' : 'Нет соединения с сервером');
      return;
    }

    setPendingAction({ type: 'direct', port, pwd });
    setIsTermsModalOpen(true);
  }, [serverInfo, isAuthenticated, isConnected, showToast, isEn, triggerGoogleLogin, addLog]);

  // Real Shell Command Execution Handler
  const handleSendCommand = useCallback((cmd) => {
    if (!cmd) return;
    const host = cleanIp(targetIp || serverInfo?.ip || serverInfo?.hostname || 'server');
    addLog('info', `rcon@${host}:~$`, cmd, true);

    if (!isConnected || !serverInfo) {
      addLog('warn', '[ERROR]', 'Нет соединения с сервером для отправки команды.');
      return;
    }

    sendMessage(getRconAppEndpoint(RCON_TOPICS.APP_COMMAND), {
      ip: host,
      port: rconHackedData?.port || Number(selectedPort) || 25575,
      password: rconHackedData?.password || '',
      command: cmd
    });
  }, [targetIp, serverInfo, isConnected, sendMessage, rconHackedData, selectedPort, addLog]);

  const handleClearLogs = useCallback(() => {
    setLogs([]);
    addLog('info', '[CLEARED]', isEn ? 'Process log cleared.' : 'Журнал процесса очищен.');
    showToast(isEn ? 'Console cleared' : 'Консоль очищена');
  }, [addLog, isEn, showToast]);

  const handleCopyLogs = useCallback(() => {
    const text = logs.map(l => `${l.time} ${l.tagText} ${l.text}`).join('\n');
    navigator.clipboard?.writeText(text);
    showToast(isEn ? 'Logs copied to clipboard' : 'Журнал скопирован в буфер обмена');
  }, [logs, isEn, showToast]);

  return (
    <PageTransition>
      <div className="rcon-page-container rcon-page-root min-h-screen text-white relative font-sans">
        <div className="relative z-10 max-w-[1320px] mx-auto px-4 sm:px-6 pt-8 sm:pt-14 pb-12 sm:pb-16">

          {/* 1. Hero Block: Left aligned in Bebas Neue */}
          <RconHero />

          {/* 2. Shared Server MOTD Card (full-width 1320px, real serverInfo) */}
          <ServerInfoCard className="w-full mx-auto relative z-40 mb-5 sm:mb-[34px]" />

          {/* 3. Pipeline Stages Tracker */}
          <RconStagesTracker currentStage={currentStage} />

          {/* 4. Main Grid: Left Column (460px) + Right Column (Terminal) */}
          <div className="rcon-main-grid">

            {/* Left Column: Direct Connect + Scanner Settings */}
            <div className="control-cards-column">
              <RconDirectConnect
                onDirectConnect={handleDirectConnect}
                disabled={isRunning}
              />

              <RconScannerSettings
                portStart={portStart}
                setPortStart={setPortStart}
                portEnd={portEnd}
                setPortEnd={setPortEnd}
                threads={threads}
                setThreads={setThreads}
                selectedDict={selectedDict}
                setSelectedDict={setSelectedDict}
                isMutator={isMutator}
                setIsMutator={setIsMutator}
                isRunning={isRunning}
                onToggleAudit={handleToggleAudit}
                currentStage={currentStage}
                availableWordlists={availableWordlists}
                onPresetChange={(type, preset) => {
                  showToast(isEn ? `Preset ${type.toUpperCase()}: ${preset.start}–${preset.end}` : `Пресет ${type.toUpperCase()}: ${preset.start}–${preset.end}`);
                }}
              />
            </div>

            {/* Right Column: Terminal Process Log */}
            <div className="flex flex-col gap-4 min-w-0">
              <MusicControlPanel
                isPlaying={isRunning}
                volume={music.volume}
                setVolume={music.setVolume}
                isMuted={music.isMuted}
                setIsMuted={music.setIsMuted}
                currentTime={music.currentTime}
                currentTrack={music.currentTrack}
                lyrics={music.lyrics}
                analyser={music.analyser}
                accentColor="purple"
                onNextTrack={music.nextTrack}
              />
              <GameContainer
                isActive={isRunning}
                accentColor="purple"
              />
              <RconTerminal
                logs={logs}
                speed={speed}
                testedCount={testedCount}
                portsFound={portsFoundCount}
                sessionTime={formatSessionTime(sessionSeconds)}
                currentStage={currentStage}
                targetHost={fullTargetAddress || (isEn ? 'Target server not connected' : 'Сервер не подключен')}
                foundPorts={foundPorts}
                selectedPort={selectedPort}
                onSelectPort={(port) => {
                  setSelectedPort(port);
                  showToast(isEn ? `Selected port: ${port}` : `Выбран порт: ${port}`);
                  addLog('info', '[TARGET]', `Выбран целевой порт RCON: ${port}`);
                }}
                onClearLogs={handleClearLogs}
                onCopyLogs={handleCopyLogs}
                onSendCommand={handleSendCommand}
                isProcessing={isRunning}
              />
            </div>

          </div>

          {/* 5. Pro Tips Section */}
          <RconProTips />

        </div>

        {/* Legal Terms of Service Modal */}
        <TermsOfServiceModal
          isOpen={isTermsModalOpen}
          onClose={() => {
            setIsTermsModalOpen(false);
            setPendingAction(null);
          }}
          onAccept={() => {
            setIsTermsModalOpen(false);
            if (pendingAction === 'audit') {
              startAudit();
            } else if (pendingAction && pendingAction.type === 'direct') {
              executeDirectConnect(pendingAction.port, pendingAction.pwd);
            }
            setPendingAction(null);
          }}
          onDecline={() => {
            setIsTermsModalOpen(false);
            setPendingAction(null);
          }}
        />
      </div>
    </PageTransition>
  );
};

export default RconHackingPage;